// src/components/FilterSidebar.jsx
import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getMakes,
  getModels,
  getColors,
  getBodyTypes,
  getFuelTypes,
  getTransmissions,
  getMileageBuckets,
} from "../api/cars";
import { useFilters } from "../stores/useFilters";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { BsCarFrontFill, BsFuelPump } from "react-icons/bs";
import { MdOutlineUpdate, MdOutlineColorLens } from "react-icons/md";
import { PiCarLight, PiSpeedometerLight } from "react-icons/pi";
import { GiGearStickPattern } from "react-icons/gi";

export default function FilterSidebar() {
  const { values, set, reset } = useFilters();

  // Safely coerce multi‐select filters into arrays
  const safeBodyType = Array.isArray(values.bodyType) ? values.bodyType : [];
  const safeFuelType = Array.isArray(values.fuelType) ? values.fuelType : [];
  const safeTransmissionType = Array.isArray(values.transmissionType)
    ? values.transmissionType
    : [];

  const safeModel = Array.isArray(values.model) ? values.model : [];
  // console.log("values", values);
  // console.log("safeModel", safeModel);

  // ─── 1) Makes (no dependencies on other filters)
  const { data: makes = [] } = useQuery({
    queryKey: ["makes"],
    queryFn: getMakes,
    staleTime: 300_000,
  });

  // ─── 2) Models (depends only on values.make + other filters implicitly if desired)
  const { data: models = [] } = useQuery({
    queryKey: ["models", values.make],
    queryFn: () => getModels(values.make),
    enabled: !!values.make,
    staleTime: 300_000,
  });

  // ─── 3) Colors (filtered by every other active facet)
  const { data: colors = [] } = useQuery({
    queryKey: [
      "colors",
      values.make, // Make filter
      // values.model, // Model filter
      safeModel.join(","), // 🔑 serialize the array
      // safeBodyType.join(","), // BodyType filter
      values.color, // Current “color” selection (if any)
      safeFuelType.join(","), // FuelType filter
      // safeTransmissionType.join(","), // Transmission filter
      values.mileageBucket, // MileageBucket filter
      values.year, // Year filter
      values.minPrice, // MinPrice filter
      values.maxPrice, // MaxPrice filter
    ],
    queryFn: () =>
      getColors({
        make: values.make,
        // model: values.model,
        model: safeModel, // ✅ actual array for API
        // bodyType: safeBodyType,
        color: values.color,
        fuelType: safeFuelType,
        // transmissionType: safeTransmissionType,
        mileageBucket: values.mileageBucket,
        year: values.year,
        minPrice: values.minPrice,
        maxPrice: values.maxPrice,
      }),
    staleTime: 300_000,
  });

  // ─── 4) Body Types (group by bodyType, but filtered by all other facets – *not* itself)
  const { data: bodyTypes = [] } = useQuery({
    queryKey: [
      "bodyTypes",
      values.make, // Apply make filter
      // values.model, // Apply model filter
      safeModel.join(","), // 🔑 serialize the array
      values.color, // Apply color filter
      safeFuelType.join(","), // Apply fuelType filter
      // safeTransmissionType.join(","), // Apply transmission filter
      values.mileageBucket, // Apply mileageBucket filter
      values.year, // Apply year filter
      values.minPrice, // Apply minPrice filter
      values.maxPrice, // Apply maxPrice filter
    ],
    queryFn: () =>
      getBodyTypes({
        make: values.make,
        // model: values.model,
        model: safeModel,
        color: values.color,
        fuelType: safeFuelType,
        // transmissionType: safeTransmissionType,
        mileageBucket: values.mileageBucket,
        year: values.year,
        minPrice: values.minPrice,
        maxPrice: values.maxPrice,
      }),
    staleTime: 300_000,
  });

  // ─── 5) Fuel Types (filtered by every other facet, including bodyType)
  const { data: fuelTypes = [] } = useQuery({
    queryKey: [
      "fuelTypes",
      values.make,
      // values.model,
      safeModel.join(","), // 🔑 serialize the array
      values.color,
      // safeBodyType.join(","), // BodyType filter
      // safeTransmissionType.join(","), // Transmission filter
      values.mileageBucket,
      values.year,
      values.minPrice,
      values.maxPrice,
    ],
    queryFn: () =>
      getFuelTypes({
        make: values.make,
        // model: values.model,
        model: safeModel,
        color: values.color,
        // bodyType: safeBodyType,
        // transmissionType: safeTransmissionType,
        mileageBucket: values.mileageBucket,
        year: values.year,
        minPrice: values.minPrice,
        maxPrice: values.maxPrice,
      }),
    staleTime: 300_000,
  });

  // ─── 6) Transmissions (filtered by every other facet, including bodyType & fuelType)
  const { data: transmissions = [] } = useQuery({
    queryKey: [
      "transmissions",
      values.make,
      // values.model,
      safeModel.join(","), // 🔑 serialize the array
      values.color,
      // safeBodyType.join(","), // BodyType filter
      safeFuelType.join(","), // FuelType filter
      values.mileageBucket,
      values.year,
      values.minPrice,
      values.maxPrice,
    ],
    queryFn: () =>
      getTransmissions({
        make: values.make,
        // model: values.model,
        model: safeModel,
        color: values.color,
        // bodyType: safeBodyType,
        fuelType: safeFuelType,
        mileageBucket: values.mileageBucket,
        year: values.year,
        minPrice: values.minPrice,
        maxPrice: values.maxPrice,
      }),
    staleTime: 300_000,
  });

  // ─── 7) Mileage Buckets (filtered by every other facet, including bodyType, fuelType, transmission)
  const { data: mileageBuckets = [] } = useQuery({
    queryKey: [
      "mileageBuckets",
      values.make,
      // values.model,
      safeModel.join(","), // 🔑 serialize the array
      values.color,
      // safeBodyType.join(","),
      safeFuelType.join(","),
      // safeTransmissionType.join(","),
      values.year,
      values.minPrice,
      values.maxPrice,
    ],
    queryFn: () =>
      getMileageBuckets({
        make: values.make,
        // model: values.model,
        model: safeModel,
        color: values.color,
        // bodyType: safeBodyType,
        fuelType: safeFuelType,
        // transmissionType: safeTransmissionType,
        year: values.year,
        minPrice: values.minPrice,
        maxPrice: values.maxPrice,
      }),
    staleTime: 300_000,
  });

  // Build “chips” for any active filters (so we can show “Make: BMW”, “BodyType: Coupe”, etc.)
  const chips = Object.entries(values).flatMap(([key, val]) =>
    Array.isArray(val) ? val.map((v) => [key, v]) : val ? [[key, val]] : []
  );

  return (
    <aside className="sticky top-4 space-y-6 pb-28 bg-white p-4 rounded-lg">
      {/* Header + Clear All */}
      <div className="flex justify-between items-center">
        <h5 className="text-lg font-bold text-primary">
          Filters ({chips.length})
        </h5>
        {chips.length > 0 && (
          <button
            onClick={reset}
            className="text-sm text-accent hover:underline focus:outline-none focus:ring-2 focus:ring-accent rounded"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Active Chips */}
      <div className="flex flex-wrap gap-2 border-b-2 border-gray pb-5">
        {chips.map(([key, val], i) => (
          <button
            key={`${key}-${val}-${i}`}
            onClick={() => {
              if (Array.isArray(values[key])) {
                set(
                  key,
                  values[key].filter((x) => x !== val)
                );
              } else {
                set(key, "");
              }
            }}
            className="flex items-center bg-primary px-2 py-1 rounded-full text-sm transition"
          >
            <span>{val}</span>
            <span className="ml-1 cursor-pointer">&times;</span>
          </button>
        ))}
      </div>

      {/* Make & Model */}
      <details className="border-b-2 border-gray pb-5">
        <summary
          className="relative flex items-center justify-between px-4 py-2
                      text-lg font-bold text-primary cursor-pointer
                      focus:outline-none focus:ring-2 focus:ring-primary
                      rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <BsCarFrontFill className="h-5 w-5 text-primary" />
            <span>Make and model</span>
          </div>

          {/* both icons are rendered, but CSS will show/hide depending on [open] */}
          <ChevronDownIcon className="h-5 w-5 text-primary details-icon-closed" />
          <ChevronUpIcon className="h-5 w-5 text-primary details-icon-open hidden" />
        </summary>

        <div className="p-4 space-y-4 text-primary">
          {/* Make dropdown */}
          <div>
            <label className="block mb-1 text-sm font-medium">Make</label>
            <select
              value={values.make}
              onChange={(e) => {
                set("make", e.target.value);
                set("model", []); // Clear model when make changes
              }}
              className="w-full px-3 py-2 border border-[#B2B2B2] rounded focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Any</option>
              {makes.map(({ value, count }) => (
                <option key={value} value={value}>
                  {value} {count > 0 && `(${count})`}
                </option>
              ))}
            </select>
          </div>

          {/* Model dropdown */}
          {/* Model checkboxes */}
          <div>
            <label className="block mb-1 text-sm text-primary font-medium">
              Model
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto border rounded p-2">
              {models.map(({ value, count }) => {
                const checked = safeModel.includes(value);
                return (
                  <label
                    key={value}
                    className="flex items-center space-x-2 text-primary"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...safeModel, value]
                          : safeModel.filter((x) => x !== value);
                        set("model", next);
                      }}
                      className="h-4 w-4 text-primary border-[#B2B2B2] rounded focus:ring-primary"
                    />
                    <span>
                      {value} {count > 0 && `(${count})`}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* <div>
            <label className="block mb-1 text-sm text-primary font-medium">
              Model
            </label>
            <select
              value={values.model}
              onChange={(e) => set("model", e.target.value)}
              disabled={!values.make}
              className="w-full px-3 py-2 border border-[#B2B2B2] rounded text-on-surface disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Any</option>
              {models.map(({ value, count }) => (
                <option key={value} value={value}>
                  {value} {count > 0 && `(${count})`}
                </option>
              ))}
            </select>
          </div> */}
        </div>
      </details>

      {/* Price Range */}
      <details className="border-b-2 border-gray pb-5">
        <summary
          className="relative flex items-center justify-between px-4 py-2
                      text-lg font-bold text-primary cursor-pointer
                      focus:outline-none focus:ring-2 focus:ring-primary
                      rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m6.621 9.879a3 3 0 0 0-5.02 2.897l.164.609a4.5 4.5 0 0 1-.108 2.676l-.157.439.44-.22a2.863 2.863 0 0 1 2.185-.155c.72.24 1.507.184 2.186-.155L15 18M8.25 15.75H12m-1.5-13.5H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
              />
            </svg>

            <span>Price</span>
          </div>

          {/* both icons are rendered, but CSS will show/hide depending on [open] */}
          <ChevronDownIcon className="h-5 w-5 text-primary details-icon-closed" />
          <ChevronUpIcon className="h-5 w-5 text-primary details-icon-open hidden" />
        </summary>
        <div className="p-4 space-y-4">
          {["minPrice", "maxPrice"].map((field) => (
            <div key={field}>
              <label className="block mb-1 text-sm text-primary font-medium">
                {field === "minPrice" ? "Min Price" : "Max Price"}
              </label>
              <input
                type="number"
                placeholder={field === "minPrice" ? "e.g. 5000" : "e.g. 30000"}
                value={values[field] || ""}
                onChange={(e) => set(field, e.target.value)}
                className="w-full px-3 py-2 border border-[#B2B2B2] rounded text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          ))}
        </div>
      </details>

      {/* Year */}
      <details className="border-b-2 border-gray pb-5">
        <summary
          className="relative flex items-center justify-between px-4 py-2
                      text-lg font-bold text-primary cursor-pointer
                      focus:outline-none focus:ring-2 focus:ring-primary
                      rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <MdOutlineUpdate className="h-5 w-5 text-primary" />
            <span>Year</span>
          </div>

          {/* both icons are rendered, but CSS will show/hide depending on [open] */}
          <ChevronDownIcon className="h-5 w-5 text-primary details-icon-closed" />
          <ChevronUpIcon className="h-5 w-5 text-primary details-icon-open hidden" />
        </summary>
        <div className="p-4">
          {/* <label className="block mb-1 text-sm text-primary font-medium">
            Year
          </label> */}
          <input
            type="number"
            placeholder="e.g. 2022"
            value={values.year || ""}
            onChange={(e) => set("year", e.target.value)}
            className="w-full px-3 py-2 border border-[#B2B2B2] rounded text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </details>

      {/* Colour */}
      <details className="border-b-2 border-gray pb-5">
        <summary
          className="relative flex items-center justify-between px-4 py-2
                      text-lg font-bold text-primary cursor-pointer
                      focus:outline-none focus:ring-2 focus:ring-primary
                      rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <MdOutlineColorLens className="h-5 w-5 text-primary" />
            <span>Colour</span>
          </div>

          {/* both icons are rendered, but CSS will show/hide depending on [open] */}
          <ChevronDownIcon className="h-5 w-5 text-primary details-icon-closed" />
          <ChevronUpIcon className="h-5 w-5 text-primary details-icon-open hidden" />
        </summary>
        <div className="p-4">
          <select
            value={values.color}
            onChange={(e) => set("color", e.target.value)}
            className="w-full px-3 py-2 border border-[#B2B2B2] rounded text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Any</option>
            {colors.map(({ value, count }) => (
              <option key={value} value={value}>
                {value} {count > 0 && `(${count})`}
              </option>
            ))}
          </select>
        </div>
      </details>

      {/* Fuel Type */}
      <details className="border-b-2 border-gray pb-5">
        <summary
          className="relative flex items-center justify-between px-4 py-2
                      text-lg font-bold text-primary cursor-pointer
                      focus:outline-none focus:ring-2 focus:ring-primary
                      rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <BsFuelPump className="h-5 w-5 text-primary" />
            <span>Fuel Type</span>
          </div>

          {/* both icons are rendered, but CSS will show/hide depending on [open] */}
          <ChevronDownIcon className="h-5 w-5 text-primary details-icon-closed" />
          <ChevronUpIcon className="h-5 w-5 text-primary details-icon-open hidden" />
        </summary>
        <div className="p-4 space-y-2">
          {fuelTypes.map(({ value, count }) => {
            const checked = safeFuelType.includes(value);
            return (
              <label
                key={value}
                className="flex items-center space-x-2 text-primary"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...safeFuelType, value]
                      : safeFuelType.filter((x) => x !== value);
                    set("fuelType", next);
                  }}
                  className="h-4 w-4 text-primary border-[#B2B2B2] rounded focus:ring-primary"
                />
                <span>
                  {value} {count > 0 && `(${count})`}
                </span>
              </label>
            );
          })}
        </div>
      </details>

      {/* Body Type */}
      <details className="border-b-2 border-gray pb-5">
        <summary
          className="relative flex items-center justify-between px-4 py-2
                      text-lg font-bold text-primary cursor-pointer
                      focus:outline-none focus:ring-2 focus:ring-primary
                      rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <PiCarLight className="h-5 w-5 text-primary" />
            <span>Body Type</span>
          </div>

          <ChevronDownIcon className="h-5 w-5 text-primary details-icon-closed" />
          <ChevronUpIcon className="h-5 w-5 text-primary details-icon-open hidden" />
        </summary>
        <div className="p-4 space-y-2">
          {bodyTypes.map(({ value, count }) => {
            const checked = safeBodyType.includes(value);
            return (
              <label
                key={value}
                className="flex items-center space-x-2 text-primary"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...safeBodyType, value]
                      : safeBodyType.filter((x) => x !== value);
                    set("bodyType", next);
                  }}
                  className="h-4 w-4 text-primary border-[#B2B2B2] rounded focus:ring-primary"
                />
                <span>
                  {value} {count > 0 && `(${count})`}
                </span>
              </label>
            );
          })}
        </div>
      </details>

      {/* Transmission */}
      <details className="border-b-2 border-gray pb-5">
        <summary
          className="relative flex items-center justify-between px-4 py-2
                      text-lg font-bold text-primary cursor-pointer
                      focus:outline-none focus:ring-2 focus:ring-primary
                      rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <GiGearStickPattern className="h-5 w-5 text-primary" />
            <span>Transmission</span>
          </div>

          <ChevronDownIcon className="h-5 w-5 text-primary details-icon-closed" />
          <ChevronUpIcon className="h-5 w-5 text-primary details-icon-open hidden" />
        </summary>
        <div className="p-4 space-y-2">
          {transmissions.map(({ value, count }) => {
            const checked = safeTransmissionType.includes(value);
            return (
              <label
                key={value}
                className="flex items-center space-x-2 text-primary"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...safeTransmissionType, value]
                      : safeTransmissionType.filter((x) => x !== value);
                    set("transmissionType", next);
                  }}
                  className="h-4 w-4 text-primary border-[#B2B2B2] rounded focus:ring-primary"
                />
                <span>
                  {value} {count > 0 && `(${count})`}
                </span>
              </label>
            );
          })}
        </div>
      </details>

      {/* Mileage */}
      <details className="border-b-2 border-gray pb-5">
        <summary
          className="relative flex items-center justify-between px-4 py-2
                      text-lg font-bold text-primary cursor-pointer
                      focus:outline-none focus:ring-2 focus:ring-primary
                      rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <PiSpeedometerLight className="h-5 w-5 text-primary" />
            <span>Mileage</span>
          </div>

          <ChevronDownIcon className="h-5 w-5 text-primary details-icon-closed" />
          <ChevronUpIcon className="h-5 w-5 text-primary details-icon-open hidden" />
        </summary>
        <div className="p-4">
          <select
            value={values.mileageBucket}
            onChange={(e) => set("mileageBucket", e.target.value)}
            className="w-full px-3 py-2 border border-[#B2B2B2] rounded text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Any</option>
            {mileageBuckets.map(({ value, label, count }) => (
              <option key={value} value={value}>
                {label} ({count})
              </option>
            ))}
          </select>
        </div>
      </details>
    </aside>
  );
}
